// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getQuoteDetails from "@adl/App/Handlers/CustomQuote/GetCustomQuote";
import updateQuoteLine from "@adl/App/Handlers/CustomQuote/UpdateQuoteLine";
import { CustomQuoteLinesModel, CustomQuoteProductInfo } from "@adl/App/Services/CustomQuotesService";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { iconLinkCommonStyles } from "@adl/XDL/Utilities";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Percent from "@insite/mobius/Icons/Percent";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    productsInfos: CustomQuoteProductInfo[];
    quoteId: string;
    quoteLines: CustomQuoteLinesModel[];
    isApplyAllMargins: boolean;
    // to know if calculating on linelevel or quote level
}

const mapStateToProps = (state: CustomAppState) => ({
    sortBy: state.xMyCustomQuoteDetails.sortBy,
});

const mapDispatchToProps = {
    updateQuoteLine,
    getQuoteDetails,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface AddItemToQuoteStyles {
    calculateMarginLink?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    textGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
}

const addItemToQuoteStyles: AddItemToQuoteStyles = {
    container: {
        css: css`
            display: flex;
            width: 100%;
        `,
    },
    calculateMarginLink: {
        ...iconLinkCommonStyles(Percent),
        css: css`
            white-space: nowrap;
        `,
    },
    modal: {
        sizeVariant: "small",
    },
    textGridItem: {
        width: 12,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
};

const styles = addItemToQuoteStyles;

const CalculateMargin: FC<Props> = ({
    quoteId,
    updateQuoteLine,
    getQuoteDetails,
    productsInfos,
    quoteLines,
    isApplyAllMargins,
    sortBy,
}) => {
    const [calculateMarginModalIsOpen, setCalculateMarginModalIsOpen] = useState(false);
    const [marginPercent, setMarginPercent] = useState("");

    const handleMarginChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!/^[0-9\b]+$/.test(event.target.value) && event.target.value !== "") {
            return;
        }
        setMarginPercent(event.target.value);
    };

    const handleMarginBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const price = parseInt(event.target.value, 10);
        if (Number.isNaN(price)) {
            setMarginPercent(quoteLines[0].extendedPrice.toString());
            return;
        }
        setMarginPercent(price.toString());
    };

    const applyMargin = () => {
        if (!productsInfos) {
            return;
        }
        setCalculateMarginModalIsOpen(false);
        setMarginPercent("");
        const margin = parseFloat(marginPercent) / 100;
        const price = productsInfos[0].quotePrice || productsInfos[0]?.pricing?.unitNetPrice;
        const quotedPrice = margin * (price || 1) + (price || 0);
        updateQuoteLine({
            updateNote: false,
            updateQty: false,
            updatePrice: true,
            linesModel: [
                {
                    lineId: productsInfos[0]?.lineId,
                    price: quotedPrice,
                },
            ],
            onSuccess(result: any) {
                getQuoteDetails({
                    quoteId,
                    skipPricingAndInventory: true,
                    sortBy,
                });
            },
        });
    };

    const applyAllMargins = () => {
        if (!productsInfos) {
            return;
        }
        setMarginPercent("");
        setCalculateMarginModalIsOpen(false);
        const margin = parseFloat(marginPercent) / 100;
        const linesModel = [] as { lineId: string; price: number }[];
        quoteLines?.forEach(line => {
            const productPrice =
                line.quotePrice || productsInfos.find(product => line.lineId === product.lineId)?.pricing?.unitNetPrice;

            const quotedPrice = margin * (productPrice || 1) + (productPrice || 0);
            linesModel.push({ lineId: line.lineId, price: quotedPrice });
        });
        updateQuoteLine({
            updateNote: false,
            updateQty: false,
            updatePrice: true,
            linesModel,
            onSuccess(result: any) {
                getQuoteDetails({
                    quoteId,
                    skipPricingAndInventory: true,
                    sortBy,
                });
            },
        });
    };

    return (
        <>
            <Link
                {...styles.calculateMarginLink}
                className="hide-on-small"
                onClick={() => setCalculateMarginModalIsOpen(true)}
            >
                {translate("Calculate Markup")}
            </Link>
            <Modal
                headline={translate("Add Price Markup")}
                isOpen={calculateMarginModalIsOpen}
                handleClose={() => {
                    setCalculateMarginModalIsOpen(false);
                    setMarginPercent("");
                }}
            >
                <GridContainer {...styles.container}>
                    <GridItem {...styles.textGridItem}>
                        <TextField
                            type="text"
                            label={translate("Markup %")}
                            value={marginPercent}
                            onChange={handleMarginChange}
                            onBlur={handleMarginBlur}
                        />
                    </GridItem>
                    <GridItem {...styles.buttonsGridItem}>
                        <Button
                            {...styles.cancelButton}
                            onClick={() => {
                                setCalculateMarginModalIsOpen(false);
                                setMarginPercent("");
                            }}
                        >
                            {translate("Cancel")}
                        </Button>
                        <Button {...styles.submitButton} onClick={isApplyAllMargins ? applyAllMargins : applyMargin}>
                            {translate("Apply")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CalculateMargin);
